import {
  iprLLToothData,
  iprLRToothData,
  iprULToothData,
  iprURToothData,
} from "@/shared/data/IPR-Report"
import { EIPRToothMap, IIPRDataItem, IIPRToothData } from "@/shared/types"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import classNames from "classnames"
import {
  FC,
  RefObject,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { toothIdTypes } from "../column"
import StageItemValue from "./StageItemValue"

import { useAppDispatch } from "@/core/app/hooks"
import { uploadCaseZips } from "@/core/app/slices/clinical/clinicalThunkApi"
import { caseManagement, iprModule, wasmModule } from "@/gluelayer"
import { CircularProgress, TextField } from "@mui/material"
import { useParams } from "react-router-dom"
import MyLowPolyline from "./MyLowPolyline"
import MyUpPolyline from "./MyUpPolyline"
import "./scss/index.scss"

export interface IUpdateToothDataParams {
  dataType:
    | "iprURToothDataMemo"
    | "iprULToothDataMemo"
    | "iprLRToothDataMemo"
    | "iprLLToothDataMemo"
  id: EIPRToothMap
  itemIndex: number
  value: IIPRDataItem
}
interface IProps {
  toothlist: number[]
  iprData: Record<number, IIPRDataItem[]>
  isPDF?: boolean
  onRef?: RefObject<{ onSubmit: () => void }>
}

export interface IToothIPRData {
  iprURToothDataMemo: IIPRToothData[]
  iprULToothDataMemo: IIPRToothData[]
  iprLRToothDataMemo: IIPRToothData[]
  iprLLToothDataMemo: IIPRToothData[]
}
const IPRReport: FC<IProps> = ({ toothlist, iprData, isPDF, onRef }) => {
  const dispatch = useAppDispatch()
  const myTextAreaRef = useRef<any>()
  const [notes, setNotes] = useState<string>("")
  const { patientId, caseId } = useParams()
  const [toothIPRData, setToothIPRData] = useState<IToothIPRData>({
    iprURToothDataMemo: [],
    iprULToothDataMemo: [],
    iprLRToothDataMemo: [],
    iprLLToothDataMemo: [],
  })

  useEffect(() => {
    const res = iprModule.getIPRMessage()
    setNotes(res)
  }, [])

  /**
   * Get Slice Tooth Id Types
   */
  const getSliceToothIdTypes = (start: number, end: number) => {
    const Types = Object.values(toothIdTypes)
    return Types.slice(start, end)
  }

  /**
   * Get Stage List
   */
  const getStageList = (type: "up" | "low") => {
    if (!iprData || !Object.values(iprData).length) {
      return []
    }
    const iprValuesObj =
      type === "up"
        ? Object.values(iprData).slice(0, 16)
        : Object.values(iprData).slice(16, 32)
    const iprValues = iprValuesObj.flatMap((item) => item)
    const stageValues = iprValues.map((item) => item.stage)
    const uniqueStageValues = [...new Set(stageValues)].sort((a, b) => a - b)
    return uniqueStageValues
  }

  const getIprURToothData = () => {
    const IPRData = [...iprURToothData].reverse()
    const types = getSliceToothIdTypes(0, 8)
    const stageList = getStageList("up")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 1] || []).map((i) => ({
        ...i,
        actualValue:
          i.actualValue && Number(i.actualValue) !== 0
            ? Number(i.actualValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualValue: "", isActive: 0 })
          }
        })
      }

      return {
        ...item,
        isMissingTooth: !types[index].some((item) => toothlist.includes(item)),
        //@ts-ignore
        data: itemData.sort((a, b) => b.stage - a.stage),
      }
    })
    return mapData
  }

  const getIprULToothData = () => {
    const IPRData = [...iprULToothData]
    const types = getSliceToothIdTypes(8, 16)
    const stageList = getStageList("up")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 9] || []).map((i) => ({
        ...i,
        actualValue:
          i.actualValue && Number(i.actualValue) !== 0
            ? Number(i.actualValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth: !types[index].some((item) => toothlist.includes(item)),
        //@ts-ignore
        data: itemData.sort((a, b) => b.stage - a.stage),
      }
    })
    return mapData
  }
  const getIprLLToothData = () => {
    const IPRData = [...iprLLToothData]
    const types = getSliceToothIdTypes(16, 24)
    const stageList = getStageList("low")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 17] || []).map((i) => ({
        ...i,
        actualValue:
          i.actualValue && Number(i.actualValue) !== 0
            ? Number(i.actualValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth: !types[index].some((item) => toothlist.includes(item)),
        //@ts-ignore
        data: itemData.sort((a, b) => a.stage - b.stage),
      }
    })
    return mapData
  }

  const getIprLRToothData = () => {
    const IPRData = [...iprLRToothData].reverse()

    const types = getSliceToothIdTypes(24, 32)
    const stageList = getStageList("low")
    const mapData = IPRData.map((item, index) => {
      let itemData = (iprData[index + 25] || []).map((i) => ({
        ...i,
        actualValue:
          i.actualValue && Number(i.actualValue) !== 0
            ? Number(i.actualValue).toFixed(1)
            : "",
        isActive: i.isActive || 0,
      }))
      if (stageList.length) {
        stageList.forEach((stage) => {
          const isAdd = !itemData.some((i) => i.stage === stage)
          if (isAdd) {
            itemData.push({ stage, value: "", actualValue: "", isActive: 0 })
          }
        })
      }
      return {
        ...item,
        isMissingTooth: !types[index].some((item) => toothlist.includes(item)),
        //@ts-ignore
        data: itemData.sort((a, b) => a.stage - b.stage),
      }
    })
    return mapData
  }

  useEffect(() => {
    const iprURToothDataMemo = getIprURToothData()
    const iprULToothDataMemo = getIprULToothData()
    const iprLRToothDataMemo = getIprLRToothData()
    const iprLLToothDataMemo = getIprLLToothData()

    setToothIPRData({
      iprURToothDataMemo,
      iprULToothDataMemo,
      iprLRToothDataMemo,
      iprLLToothDataMemo,
    })
  }, [iprData])

  /**
   * Upper Stage Value
   */
  const upperStageValue = useMemo(() => {
    const upData = [
      ...toothIPRData.iprURToothDataMemo,
      ...toothIPRData.iprULToothDataMemo,
    ]
    const stageValues = upData.map((item) => item.data).flatMap((item) => item)
    const plannedValues =
      stageValues.reduce((acc, cur) => {
        return acc + Number(cur.value)
      }, 0) || 0
    const actuallyPlacedValues =
      stageValues.reduce((acc, cur) => {
        const newCur = cur.isActive ? cur.value : cur.actualValue
        return acc + Number(newCur)
      }, 0) || 0

    return {
      plannedValues: plannedValues.toFixed(1),
      actuallyPlacedValues: actuallyPlacedValues.toFixed(1),
    }
  }, [toothIPRData.iprURToothDataMemo, toothIPRData.iprULToothDataMemo])

  /**
   * lower Stage Value
   */
  const lowerStageValue = useMemo(() => {
    const upData = [
      ...toothIPRData.iprLRToothDataMemo,
      ...toothIPRData.iprLLToothDataMemo,
    ]
    const stageValues = upData.map((item) => item.data).flatMap((item) => item)
    const plannedValues =
      stageValues.reduce((acc, cur) => {
        return acc + Number(cur.value)
      }, 0) || 0
    const actuallyPlacedValues =
      stageValues.reduce((acc, cur) => {
        const newCur = cur.isActive ? cur.value : cur.actualValue
        return acc + Number(newCur)
      }, 0) || 0

    return {
      plannedValues: plannedValues.toFixed(1),
      actuallyPlacedValues: actuallyPlacedValues.toFixed(1),
    }
  }, [toothIPRData.iprLRToothDataMemo, toothIPRData.iprLLToothDataMemo])

  const updateToothData = (data: IUpdateToothDataParams) => {
    const newData = toothIPRData[data.dataType].map((item) => {
      if (item.id === data.id) {
        return {
          ...item,
          data: item.data.map((i, index) => {
            if (index === data.itemIndex) {
              return {
                ...i,
                ...data.value,
              }
            }
            return i
          }),
        }
      }
      return item
    })
    setToothIPRData({
      ...toothIPRData,
      [data.dataType]: newData,
    })
  }

  /**
   * Upload
   */
  const upload = () => {
    try {
      if (!wasmModule.isFinishedOpencase) return
      caseManagement.getCaseFiles(["bulk2"]).then((r) => {
        const zips = []
        if (r) {
          for (const k in r) {
            zips.push({
              fileName: k,
              file: r[k],
            })
          }
          dispatch(
            uploadCaseZips({
              zips,
              patientId,
              caseId,
            }),
          )
        }
      })
    } catch (e) {
      console.log("upload error", e)
    }
  }

  const onSubmit = () => {
    let objData = {}

    const toothlist1 = toothIPRData.iprURToothDataMemo.map((item) => item.data)
    const toothlist2 = toothIPRData.iprULToothDataMemo.map((item) => item.data)
    const toothlist3 = toothIPRData.iprLRToothDataMemo.map((item) => item.data)
    const toothlist4 = toothIPRData.iprLLToothDataMemo.map((item) => item.data)

    const toothlist = [
      ...toothlist1,
      ...toothlist2,
      ...toothlist3,
      ...toothlist4,
    ]
    const toothlistMap = toothlist.map((item) => {
      const toothItem = item.map((i) => {
        return {
          ...i,
          value: Number(i.value) ? Number(i.value).toFixed(1) : "0",
          actualValue: Number(i.actualValue)
            ? Number(i.actualValue).toFixed(1)
            : "0",
          isActive: i.isActive || 0,
        }
      })
      return toothItem
    })
    toothlistMap.forEach((item, index) => {
      objData[index + 1] = item
    })
    // console.log("==========objData", JSON.stringify(objData))
    const jsonData = JSON.stringify(objData)
    const res = caseManagement.setActualIPRValue(jsonData)
    if (res) {
      upload()
    }
  }

  /**
   * TextArea onKeyDown
   */
  const onKeyDown = (e) => {
    if ((e.keyCode === 27 || e.keyCode === 13) && notes) {
      const res = iprModule.setIPRMessage(notes)
      if (res) {
        myTextAreaRef.current?.blur()
        upload()
      }
    }
  }

  useImperativeHandle(onRef, () => {
    // 需要将暴露的接口返回出去
    return {
      onSubmit: () => {
        return onSubmit()
      },
    }
  })
  return (
    <>
      {Object.values(toothIPRData)[0].length > 0 ? (
        <div
          className="ipr-report"
          style={{ userSelect: "none" }}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => e.preventDefault()}
        >
          <div className="ipr-report-tooltip">
            <div className="d-flex gap-50">
              <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
              IPR should be scheduled before indicated stage starts.
            </div>
          </div>

          {isPDF && (
            <div className="pdf-desc">
              Before performing Interproximal Reduction (IPR), the clinician
              must verify that the Patient Name and Case Number corresponds to
              the correct patient. ulab Systems, Inc, assumes no responsibility
              or liability for any damages or claims arising from the
              clinician's administration of IPR.
            </div>
          )}

          {/*Total IPR*/}
          {toothIPRData.iprURToothDataMemo[0].data.length > 0 && (
            <div className="ipr-report-header">
              <div className="ipr-report-header-total-ipr">
                <div className="total-text">Total IPR: </div>
                <div className="total-info">
                  <div className="d-flex gap-1">
                    <span>Upper: </span>
                    <span>{upperStageValue.plannedValues}mm(planned)</span>
                    <span>
                      {upperStageValue.actuallyPlacedValues}mm(actually placed)
                    </span>
                  </div>
                  <div className="d-flex gap-1">
                    <span>Lower:</span>
                    <span>{lowerStageValue.plannedValues}mm(planned)</span>
                    <span>
                      {lowerStageValue.actuallyPlacedValues}mm(actually placed)
                    </span>
                  </div>
                </div>
              </div>
              <div className="ipr-report-header-desc">
                <div className="planned-style">
                  <div className="planned-box">
                    <div className="planned-ipr-box" />
                    <div style={{ padding: "0 5px" }}>/</div>
                    <div className="mark-as-completed-box" />
                  </div>
                  Planned IPR / Mark as completed
                </div>
                <div className="actually-placed-style">Actually applied</div>
              </div>
            </div>
          )}

          <div
            className={classNames("", {
              "scroll-content": !isPDF,
              "hide-scrollbar": isPDF,
            })}
          >
            {/*Up Stage */}
            {toothIPRData.iprURToothDataMemo[0].data.length > 0 && (
              <div className="up-stage-wrap">
                {toothIPRData.iprURToothDataMemo[0].data.map(
                  (wrapItem, stageIndex) => {
                    return (
                      <div className="d-flex up-stage-item">
                        <div className="d-flex up-stage-item-num">
                          Stage {wrapItem.stage}
                        </div>
                        <div className="d-flex common-stage-polyline-wrap">
                          {toothIPRData.iprURToothDataMemo.map(
                            (item, index) => {
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    item={item.data[stageIndex]}
                                    dataType="iprURToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                  />
                                </div>
                              )
                            },
                          )}
                          {toothIPRData.iprULToothDataMemo.map(
                            (item, index) => {
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    item={item.data[stageIndex]}
                                    dataType="iprULToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                  />
                                </div>
                              )
                            },
                          )}
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            )}

            {/* IPR Content */}
            {/*没有16、32*/}
            {/* 24 特殊处理*/}
            <div
              className={classNames("ipr-reports-view", {
                "justify-content-center h-100":
                  !toothIPRData.iprURToothDataMemo[0].data.length &&
                  !toothIPRData.iprLRToothDataMemo[0].data.length,
              })}
            >
              <div className="common-tooth-group up-tooth-group">
                {toothIPRData.iprURToothDataMemo[0].data.length > 0 && (
                  <MyUpPolyline toothIPRData={toothIPRData} />
                )}

                <div className="up-right-tooth-group">
                  {toothIPRData.iprURToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                      })}
                    >
                      {item.icon}
                      <div className="up-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
                <div className="up-left-tooth-group">
                  {toothIPRData.iprULToothDataMemo.map((item, index) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                      })}
                    >
                      {item.icon}
                      <div className="up-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tooth-group-lable">
                <span className="">UR</span>
                <span className="">UL</span>
                <span className="">LR</span>
                <span className="">LL</span>
              </div>

              <div className="common-tooth-group  low-tooth-group">
                {toothIPRData.iprLRToothDataMemo[0].data.length > 0 && (
                  <MyLowPolyline toothIPRData={toothIPRData} />
                )}
                <div className="low-left-tooth-group">
                  {toothIPRData.iprLRToothDataMemo.map((item) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                      })}
                    >
                      {item.icon}
                      <div className="low-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
                <div className="low-right-tooth-group">
                  {toothIPRData.iprLLToothDataMemo.map((item) => (
                    <div
                      key={item.id}
                      className={classNames("common-tooth-items", {
                        "missing-tooth": item.isMissingTooth,
                      })}
                    >
                      {item.icon}
                      <div className="low-tooth-id">{item.id}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/*Low Stage*/}
            {toothIPRData.iprLRToothDataMemo[0].data.length > 0 && (
              <div className="low-stage-wrap">
                {toothIPRData.iprLRToothDataMemo[0].data.map(
                  (stageItem, stageIndex) => {
                    return (
                      <div className="d-flex low-stage-item">
                        <div className="d-flex low-stage-item-num">
                          Stage {stageItem.stage}
                        </div>
                        <div className="d-flex common-stage-polyline-wrap">
                          {toothIPRData.iprLRToothDataMemo.map(
                            (item, index) => {
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    item={item.data[stageIndex]}
                                    dataType="iprLRToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                  />
                                </div>
                              )
                            },
                          )}
                          {toothIPRData.iprLLToothDataMemo.map(
                            (item, index) => {
                              return (
                                <div
                                  key={item.id}
                                  className={classNames(
                                    "d-flex stage-polyline",
                                    {
                                      "diabled-missing-tooth":
                                        item.isMissingTooth,
                                    },
                                  )}
                                >
                                  <StageItemValue
                                    item={item.data[stageIndex]}
                                    dataType="iprLLToothDataMemo"
                                    id={item.id}
                                    itemIndex={stageIndex}
                                    updateToothData={updateToothData}
                                  />
                                </div>
                              )
                            },
                          )}
                        </div>
                      </div>
                    )
                  },
                )}
              </div>
            )}

            <div className="add-notes">
              <div className="add-notes-text">
                Use the text box to notate any additional detail,
                clarifications, or clinical observations.
              </div>
              <TextField
                inputRef={myTextAreaRef}
                id="outlined-multiline-flexible"
                className="add-note-input"
                placeholder="Enter notes here..."
                label=""
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="ipr-report-loading">
          <CircularProgress
            variant="indeterminate"
            disableShrink
            sx={{
              color: (theme) =>
                theme.palette.mode === "light" ? "#1a90ff" : "#308fe8",
              animationDuration: "550ms",
            }}
            size={40}
            thickness={4}
          />
        </div>
      )}
    </>
  )
}

export default IPRReport
